// This is the component that renders the tips for the home page and the results page
import React, { useEffect, useMemo, useState } from "react";
import {
  useEarlyPredictionsQuery,
  useHistoricalResultsQuery,
  useLeaderboardQuery,
  useRaceCardsQuery,
} from "@/api/vercel";
import { useExtraDetails } from "@/hooks/useExtraDetails";
import { useHotTipSettings } from "@/hooks/useHotTipSettings";
import { useTipsDisplay } from "@/hooks/useTipsDisplay";
import { usePriceDisplayMode } from "@/hooks/usePriceDisplayMode";
import { cn } from "@/lib/utils";
import { logger } from "@/utils/logger";
import formatMessageContent from "@/utils/message-markup-formatter";
import { transformRaceData } from "@/utils/transform-race-data";
import { triggerUpgradeFlow } from "@/utils/upgrade";
import { minTips, totalTips } from "@/utils/vars";
import { hasRaceResults } from "@/utils/tally-results";
import { findRaceCard } from "@/utils/race-cards";
import { formatTimeOfDay } from "@/utils/time-format";
import {
  TIP_SETS,
  buildTipSets,
  getTipMovements,
  isSameRunner,
  isScratchedInPodium,
} from "@/utils/tip-movement";
import {
  AlertTriangle,
  Clock,
  Lock,
  Share2,
} from "lucide-react";
import HorseExtraDetails from "@/components/HorseExtraDetails";
import RaceInfoPanel from "@/components/RaceInfoPanel";
import TipWinnerModal from "@/components/TipWinnerModal";
import BellIcon from "@/components/icons/bell-icon";
import ChevronDownIcon from "@/components/icons/chevron-down-icon";
import FlameIcon from "@/components/icons/flame-icon";
import MedalIcon from "@/components/icons/medal-icon";
import MovingUpIcon from "@/components/icons/moving-up-icon";
import TrendingDownIcon from "@/components/icons/trending-down-icon";
import TrophyIcon from "@/components/icons/trophy-icon";
import MarketStartTime from "@/components/market-start-time";
import TipMovementMarker from "@/components/TipMovementMarker";
import TipPriceToggle from "@/components/TipPriceToggle";
import {
  ODDS_TOTAL_HINT,
  formatOddsTotal,
  formatTipPrice,
  getOddsTotal,
  isRunnerScratched,
  pickTipPrice,
} from "@/utils/tip-odds";

// Helper function to determine confidence level based on price
const getConfidenceLevel = (price) => {
  if (price <= 3) return "high";
  if (price <= 6) return "medium";
  return "low";
};

// Helper function to compare odds and get styling
const getOddsComparison = (currentPrice, earlyPrice) => {
  // If we don't have both prices, return null
  if (!currentPrice || !earlyPrice) {
    return null;
  }

  const current = parseFloat(currentPrice);
  const early = parseFloat(earlyPrice);

  // If prices are the same, return null
  if (current === early) {
    return null;
  }

  // If current price is higher than early price (negative - odds increased)
  if (current > early) {
    return {
      type: "negative",
      backgroundColor: "#FFEAE9",
      textColor: "#D00D01",
      icon: "trending-down",
      diff: (current - early).toFixed(2),
    };
  }

  // If current price is lower than early price (positive - odds decreased)
  return {
    type: "positive",
    backgroundColor: "#E4FFE1",
    textColor: "#008100",
    icon: "moving-up",
    diff: (early - current).toFixed(2),
  };
};

// Add a function to handle opening the winner modal via localStorage
const triggerWinnerModal = (winnerData) => {
  try {
    localStorage.setItem("winner_modal_open", "true");
    localStorage.setItem("winner_modal_data", JSON.stringify(winnerData));
    // Dispatch an event to notify components listening for modal changes
    window.dispatchEvent(new Event("winner_modal_update"));
  } catch (error) {
    console.error("Error setting winner modal data:", error);
  }
};

const normalizeWhitespace = (value) =>
  `${value || ""}`.replace(/\s+/g, " ").trim();

const stripLeadingNumber = (value) => {
  const raw = normalizeWhitespace(value);
  if (!raw) return "";
  return raw.replace(/^\d+\s*[.\-_:]\s*/g, "").trim();
};

const normalizeRunnerName = (value) => stripLeadingNumber(value).toLowerCase();

const normalizeRunnerNumber = (value) => {
  if (value === null || value === undefined) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const match = raw.match(/^(\d+)/);
  const candidate = match ? match[1] : raw;
  const normalized = candidate.replace(/^0+/, "");
  return normalized || candidate;
};

const extractRunnerNumber = (runner) => {
  if (!runner) return null;
  // Common fields first
  const direct =
    runner.horseNumber ??
    runner.runnerNumber ??
    runner.number ??
    runner.saddlecloth ??
    null;
  const normalizedDirect = normalizeRunnerNumber(direct);
  if (normalizedDirect) return normalizedDirect;

  const nameFields = [
    runner.name,
    runner.runnerName,
    runner.selectionName,
    runner.formattedName,
  ];
  for (const field of nameFields) {
    if (typeof field !== "string") continue;
    const raw = field.trim();
    if (!raw) continue;
    const m = raw.match(/^(\d+)\s*[.\-_:]/) || raw.match(/^(\d+)\b/);
    if (m && m[1]) {
      const normalized = normalizeRunnerNumber(m[1]);
      if (normalized) return normalized;
    }
  }
  return null;
};

export const buildEarlyMailAssessmentHtml = (assessment) => {
  const trimmedAssessment =
    typeof assessment === "string" ? assessment.trim() : "";
  if (!trimmedAssessment) return null;

  // Supports custom markup like **bold**, *italic*, **_horse_**, and newlines
  return formatMessageContent(trimmedAssessment);
};

export default function RenderTips({
  historicalData,
  hasHistoricalData,
  selectedTrack = null,
  selectedRace = null,
  today = null,
  isTipHistoryExpanded = false,
  toggleTipHistory = () => {},
  isChanging = false,
  isMounted = false,
  isResultsPage = false,
  isEarlyMail = false,
  earlyMailRelativeLabel = "",
  showEarlyMailReview = false,
  // isLexiSocial should be true ONLY for free tier users (Lexi Social)
  // All paid products (Lexi Pro, 7-Day Pass, etc.) should have isLexiSocial = false
  isLexiSocial = false,
  // Add props that might be passed from results.jsx
  externalLeaderboardData = null,
  externalLeaderboardQueryData = null,
  externalHasEarlyPredictions = null,
  externalEarlyPredictionsData = null,
  externalHistoricalData = null,
  externalHasHistoricalData = null,
  tipsDisplayLimit = null,
  forceShowAllTips = false,
  // Tip movement markers (see src/utils/tip-movement.js). Pass only sets the viewer may see:
  // - race tips: { earlyMail: [...Early Mail tips] }
  // - Early Mail (isEarlyMail): { raceDay: raceData, latest: raceData } of the same race
  tipMovementSources = null,
  hideTipMovement = false,
  // Tells the page whether markers are shown (it draws the marker key under the race tips)
  onTipMovementShownChange = null,
  // Text toggle under each list: "$ price" <-> "odds %" (src/utils/tip-odds.js)
  showPriceToggle = false,
}) {
  // Internal state to store the transformed race entries
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [hasEarlyPredictionsState, setHasEarlyPredictionsState] =
    useState(false);
  const [internalHasHistoricalData, setInternalHasHistoricalData] =
    useState(false);

  // Only fetch data if external data is not provided
  const skipLeaderboardQuery = !!externalLeaderboardQueryData;
  const skipEarlyPredictionsQuery = !!externalEarlyPredictionsData;
  const skipHistoricalResultsQuery = !!externalHistoricalData;

  // Initialize hooks at the top level
  const { showAllTips } = useTipsDisplay();
  const effectiveShowAllTips = forceShowAllTips ? true : showAllTips;
  const normalizedTipsDisplayLimit =
    Number.isFinite(tipsDisplayLimit) && tipsDisplayLimit > 0
      ? Math.max(tipsDisplayLimit, minTips)
      : null;
  const effectiveTotalTips = normalizedTipsDisplayLimit ?? totalTips;
  const displayTipsLimit = effectiveShowAllTips ? effectiveTotalTips : minTips;
  const { hotTipMinPrice, isLoading: isHotTipSettingsLoading } =
    useHotTipSettings();
  const { showExtraDetails } = useExtraDetails();

  // Race card (prize money, class, distance) for the Extra Details panel
  const { data: raceCardsResponse } = useRaceCardsQuery({
    date: today,
    enabled: showExtraDetails,
  });
  const raceCard = useMemo(
    () =>
      showExtraDetails
        ? findRaceCard(raceCardsResponse?.data, selectedTrack, selectedRace)
        : null,
    [showExtraDetails, raceCardsResponse, selectedTrack, selectedRace],
  );

  // "$ price" <-> "odds %" for every list (Early Mail, Race Day tips, Late Mail):
  // one remembered choice, kept in step across lists (src/hooks/usePriceDisplayMode.js)
  const { mode: priceDisplayMode, toggleMode: togglePriceOddsMode } =
    usePriceDisplayMode();

  const formatPriceOrOddsPercent = (numericPrice) =>
    formatTipPrice(numericPrice, priceDisplayMode);

  // Fetch leaderboard data only if not provided externally
  const {
    data: { data: leaderboardQueryData } = {},
    isError: isLeaderboardError,
    isLoading: isLeaderboardLoading,
  } = useLeaderboardQuery({
    date: today,
    collection: "predictions",
    // Add options to prevent refetching during navigation
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    cacheTime: 30 * 60 * 1000, // Keep unused data in cache for 30 minutes
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    refetchOnMount: false, // Don't refetch when component mounts if data exists
    enabled: !skipLeaderboardQuery, // Skip query if data is provided externally
  });

  // Fetch early predictions only if not provided externally
  const {
    data: earlyPredictions = { data: {} },
    isLoading: isEarlyPredictionsLoading,
  } = useEarlyPredictionsQuery({
    date: today,
    staleTime: 10 * 60 * 1000,
    cacheTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    selectedTrack,
    selectedRace,
    enabled: !skipEarlyPredictionsQuery, // Skip query if data is provided externally
  });

  // Fetch historical results only if not provided externally
  const {
    data: histResults = { data: {} },
    isError: isHistoricalError,
    isLoading: isHistoricalLoading,
  } = useHistoricalResultsQuery({
    date: today,
    fetchAll: false,
    staleTime: 10 * 60 * 1000,
    cacheTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: !skipHistoricalResultsQuery, // Skip query if data is provided externally
  });

  // Use external data if provided, otherwise use fetched data
  const baseLeaderboardQueryData =
    externalLeaderboardQueryData || leaderboardQueryData;
  const finalEarlyPredictionsData =
    externalEarlyPredictionsData || earlyPredictions.data || {};
  const finalHistoricalData =
    externalHistoricalData || historicalData || histResults.data;

  const hasRaceResultsForCurrentRace = useMemo(
    () =>
      hasRaceResults(selectedTrack, selectedRace, today, finalHistoricalData),
    [selectedTrack, selectedRace, today, finalHistoricalData],
  );

  // Gate early predictions for Lexi Social (free tier) users: only show first two tips
  // All paid products (Lexi Pro, 7-Day Pass, etc.) get full access
  const gatedEarlyPredictionsData = useMemo(() => {
    if (!isLexiSocial) return finalEarlyPredictionsData;
    if (effectiveShowAllTips) return finalEarlyPredictionsData;
    const result = {};
    Object.entries(finalEarlyPredictionsData || {}).forEach(
      ([raceKey, raceData]) => {
        if (!raceData) return;
        const firstFour = Array.isArray(raceData.firstFourData)
          ? raceData.firstFourData.slice(0, 2)
          : [];
        result[raceKey] = {
          ...raceData,
          firstFourData: firstFour,
        };
      },
    );
    return result;
  }, [finalEarlyPredictionsData, isLexiSocial, effectiveShowAllTips]);

  // For Lexi Social, prefer (limited) early predictions pre-results; otherwise use regular
  const finalLeaderboardQueryData = useMemo(() => {
    if (isLexiSocial && !hasRaceResultsForCurrentRace) {
      // Only expose early data; never fall back to race-time tips for Lexi Social
      const raceKey = `${selectedTrack?.replace(/\s+/g, "-")}-${selectedRace}-${today}`;
      const earlyRaceData = gatedEarlyPredictionsData?.[raceKey];
      const earlyPredictionData = gatedEarlyPredictionsData?.predictionData;

      const hasEarlyRaceData =
        earlyRaceData?.firstFourData &&
        Array.isArray(earlyRaceData.firstFourData) &&
        earlyRaceData.firstFourData.length > 0;

      const hasEarlyPredictionData =
        earlyPredictionData?.firstFourData &&
        Array.isArray(earlyPredictionData.firstFourData) &&
        earlyPredictionData.firstFourData.length > 0;

      if (hasEarlyRaceData) {
        if (process.env.NODE_ENV === "development") {
          // console.info("[render-tips] using earlyRaceData for UT-FREE", {
          //   raceKey,
          //   source: "earlyRaceData",
          //   firstFourCount: earlyRaceData?.firstFourData?.length,
          // });
        }
        return gatedEarlyPredictionsData;
      }

      if (hasEarlyPredictionData) {
        if (process.env.NODE_ENV === "development") {
          console.info("[render-tips] using earlyPredictionData for UT-FREE", {
            raceKey,
            source: "predictionData",
            firstFourCount: earlyPredictionData?.firstFourData?.length,
          });
        }
        return { [raceKey]: earlyPredictionData };
      }

      // No early data available: do not expose race-time tips to Lexi Social
      if (process.env.NODE_ENV === "development") {
        console.info(
          "[render-tips] no early data for UT-FREE; returning null",
          {
            raceKey,
          },
        );
      }
      return null;
    }
    return baseLeaderboardQueryData;
  }, [
    isLexiSocial,
    baseLeaderboardQueryData,
    gatedEarlyPredictionsData,
    hasRaceResultsForCurrentRace,
    selectedTrack,
    selectedRace,
    today,
  ]);

  // Determine if we have historical data for this race
  useEffect(() => {
    if (externalHasHistoricalData !== undefined) {
      setInternalHasHistoricalData(externalHasHistoricalData);
      return;
    }

    if (hasHistoricalData !== undefined) {
      setInternalHasHistoricalData(hasHistoricalData);
      return;
    }

    if (!finalHistoricalData || !selectedTrack || !selectedRace || !today) {
      setInternalHasHistoricalData(false);
      return;
    }

    const currentRaceKey = `${selectedTrack.replace(/\s+/g, "-")}-${selectedRace}-${today}`;
    const hasHistoricalDataForCurrentRace =
      Object.keys(finalHistoricalData).includes(currentRaceKey);
    setInternalHasHistoricalData(hasHistoricalDataForCurrentRace);
  }, [
    finalHistoricalData,
    selectedTrack,
    selectedRace,
    today,
    externalHasHistoricalData,
    hasHistoricalData,
  ]);

  // Transform and set the leaderboard data when the query params or data change
  useEffect(() => {
    // If external leaderboard data is provided, use it directly
    if (externalLeaderboardData) {
      setLeaderboardData(externalLeaderboardData);
      return;
    }

    // Otherwise, process data from the query
    if (
      !finalLeaderboardQueryData ||
      !selectedTrack ||
      !selectedRace ||
      !today
    ) {
      setLeaderboardData([]);
      return;
    }

    const raceKey = `${selectedTrack.replace(/\s+/g, "-")}-${selectedRace}-${today}`;
    const raceData = finalLeaderboardQueryData?.[raceKey];

    if (raceData?.firstFourData && Array.isArray(raceData.firstFourData)) {
      const transformedData = transformRaceData(
        raceData,
        { includeConfidence: true },
        gatedEarlyPredictionsData,
      );

      // Verify we have valid entries before setting them
      const validEntries = transformedData.filter(
        (entry) =>
          entry && typeof entry === "object" && entry.name && entry.number,
      );

      if (validEntries.length > 0) {
        setLeaderboardData(validEntries);
      } else {
        setLeaderboardData([]);
      }
    } else {
      setLeaderboardData([]);
    }
  }, [finalLeaderboardQueryData, selectedTrack, selectedRace, today]);

  // Use external hasEarlyPredictions if provided, otherwise calculate it
  const hasEarlyPredictions = useMemo(() => {
    if (externalHasEarlyPredictions !== null) {
      return externalHasEarlyPredictions;
    }

    if (!gatedEarlyPredictionsData || !selectedTrack || !selectedRace)
      return false;

    const raceKey = `${selectedTrack.replace(/\s+/g, "-")}-${selectedRace}-${today}`;
    const firstFourData = gatedEarlyPredictionsData[raceKey]?.firstFourData;

    // Get tipUpdatedAt values for both datasets
    const regularTipUpdatedAt =
      finalLeaderboardQueryData?.[raceKey]?.tipUpdatedAt;
    const earlyTipUpdatedAt =
      gatedEarlyPredictionsData?.[raceKey]?.tipUpdatedAt;

    // If tipUpdatedAt values are the same, consider there are no early predictions
    if (!isLexiSocial && regularTipUpdatedAt === earlyTipUpdatedAt) {
      return false;
    }

    return (
      firstFourData && Array.isArray(firstFourData) && firstFourData.length > 0
    );
  }, [
    externalHasEarlyPredictions,
    gatedEarlyPredictionsData,
    selectedTrack,
    selectedRace,
    today,
    finalLeaderboardQueryData,
    isLexiSocial,
  ]);

  // Create state to track favorites status
  const [favoriteStatus, setFavoriteStatus] = useState({});

  // Generate the current race key
  const currentRaceKey = useMemo(() => {
    if (!selectedTrack || !selectedRace || !today) return null;
    return `${selectedTrack.replace(/\s+/g, "-")}-${selectedRace}-${today}`;
  }, [selectedTrack, selectedRace, today]);

  const earlyMailAssessmentHtml = useMemo(() => {
    if (!isEarlyMail || !showEarlyMailReview || !currentRaceKey) return null;
    const raceData =
      finalLeaderboardQueryData?.[currentRaceKey] ||
      finalLeaderboardQueryData?.predictionData ||
      finalLeaderboardQueryData?.earlyPredictionData ||
      null;

    return buildEarlyMailAssessmentHtml(raceData?.assessment || "");
  }, [
    isEarlyMail,
    showEarlyMailReview,
    finalLeaderboardQueryData,
    currentRaceKey,
  ]);

  // Load and monitor favorite status
  useEffect(() => {
    if (typeof window === "undefined" || !currentRaceKey) return;

    const checkFavoriteStatus = () => {
      try {
        const favoriteTips = JSON.parse(
          localStorage.getItem("favoriteTips") || "[]",
        );
        const isFavorited = favoriteTips.some(
          (tip) => tip.raceKey === currentRaceKey,
        );
        setFavoriteStatus((prev) => ({
          ...prev,
          [currentRaceKey]: isFavorited,
        }));
      } catch (error) {
        logger.error("Error checking favorite status:", error);
      }
    };

    // Initial check
    checkFavoriteStatus();

    // Set up event listener for storage changes
    const handleStorageChange = (e) => {
      if (e.key === "favoriteTips") {
        checkFavoriteStatus();
      }
    };

    window.addEventListener("storage", handleStorageChange);

    // Custom event for local changes within the same tab
    window.addEventListener("favoritesUpdated", checkFavoriteStatus);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("favoritesUpdated", checkFavoriteStatus);
    };
  }, [currentRaceKey]);

  // Toggle favorite status function
  const toggleFavorite = (raceKey) => {
    if (typeof window === "undefined" || !raceKey) return;

    try {
      const favoriteTips = JSON.parse(
        localStorage.getItem("favoriteTips") || "[]",
      );
      const isFavorited = favoriteTips.some((tip) => tip.raceKey === raceKey);

      const now = new Date();
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + 1);
      expiryDate.setHours(10, 0, 0, 0);

      let updatedFavorites;

      if (isFavorited) {
        // Remove from favorites
        updatedFavorites = favoriteTips.filter(
          (tip) => tip.raceKey !== raceKey,
        );
      } else {
        // Get market start time from race data
        const raceData = finalLeaderboardQueryData?.[raceKey];
        const marketStartTime = raceData?.marketStartTime || null;

        // Add to favorites
        const newFavorite = {
          raceKey,
          track: selectedTrack,
          race: selectedRace,
          date: today,
          marketStartTime: marketStartTime, // Include market start time
          tips: leaderboardData?.slice(0, effectiveTotalTips) || [],
          addedAt: now.toISOString(),
          expiresAt: expiryDate.toISOString(),
        };

        updatedFavorites = [...favoriteTips, newFavorite];
      }

      localStorage.setItem("favoriteTips", JSON.stringify(updatedFavorites));

      // Update state immediately without waiting for the effect
      setFavoriteStatus((prev) => ({ ...prev, [raceKey]: !isFavorited }));

      // Dispatch event to notify other components in the same tab
      window.dispatchEvent(new Event("favoritesUpdated"));
    } catch (error) {
      logger.error("Error updating favorites:", error);
    }
  };

  const getResultDisplay = (
    entry,
    isResult = false,
    earlyPredictionsData = null,
    isEarlyPrediction = false,
  ) => {
    // Add a safety check for entry
    if (!entry || !entry.name || !entry.number) {
      logger.warn("Invalid entry in getResultDisplay:", entry);
      return null;
    }

    if (!effectiveShowAllTips) {
      // Only process first 2 horses
      if (entry?.rank > 2 && !isEarlyPrediction) return null;
    }

    if (isResult === true) {
      const raceKey = `${selectedTrack?.replace(/\s+/g, "-")}-${selectedRace}-${today}`;
      const raceResults = finalHistoricalData?.[raceKey];

      // console.log("🔍 [render-tips] Checking scratched horses:", {
      //   raceKey,
      //   entryName: entry?.name,
      //   entryNumber: entry?.number,
      //   entryRank: entry?.rank,
      //   entrySelectionId: entry?.selectionId,
      //   hasBetfairResults: !!raceResults?.betfairResults,
      //   scratchings: raceResults?.betfairResults?.scratchings,
      //   podium: raceResults?.podium,
      //   podiumValue: raceResults?.podium?.[entry?.rank - 1],
      // });

      if (!raceResults?.betfairResults) {
        return null;
      }

      const selectionIdToRunner = (() => {
        const map = new Map();
        const sources = [
          ...(Array.isArray(raceResults?.predictionData?.firstFourData)
            ? raceResults.predictionData.firstFourData
            : []),
          ...(Array.isArray(raceResults?.earlyPredictionData?.firstFourData)
            ? raceResults.earlyPredictionData.firstFourData
            : []),
        ];
        sources.forEach((tip) => {
          const sid = tip?.selectionId ?? tip?.selectionID ?? tip?.sid ?? null;
          if (sid === null || sid === undefined) return;
          const sidStr = String(sid).trim();
          if (!sidStr) return;

          const formatted =
            typeof tip?.formattedName === "string" ? tip.formattedName : "";
          const numberFromFormatted = formatted
            ? formatted.split(".")[0]
            : null;
          const nameFromFormatted = formatted
            ? formatted.split(".").slice(1).join(" ").trim()
            : "";

          const number =
            normalizeRunnerNumber(
              tip?.number ??
                tip?.horseNumber ??
                tip?.runnerNumber ??
                numberFromFormatted,
            ) || null;
          const name = normalizeRunnerName(
            tip?.name ??
              tip?.runnerName ??
              tip?.selectionName ??
              nameFromFormatted,
          );

          map.set(sidStr, { number, name });
        });
        return map;
      })();

      const entrySelectionId =
        entry?.selectionId !== null && entry?.selectionId !== undefined
          ? String(entry.selectionId)
          : null;
      const entryNumber = normalizeRunnerNumber(entry?.number);
      const entryName = normalizeRunnerName(entry?.name);

      const runnerMatchesEntry = (runner) => {
        if (!runner) return false;

        const runnerSelectionId =
          runner?.selectionId !== null && runner?.selectionId !== undefined
            ? String(runner.selectionId)
            : // Some APIs return scratchings as raw IDs
              typeof runner === "string" || typeof runner === "number"
              ? String(runner)
              : null;

        if (
          entrySelectionId &&
          runnerSelectionId &&
          entrySelectionId === runnerSelectionId
        ) {
          return true;
        }

        // If runner is only identifiable by selectionId, map to number/name using race tips.
        if (runnerSelectionId) {
          const mapped = selectionIdToRunner.get(runnerSelectionId);
          if (mapped) {
            if (entryNumber && mapped.number && entryNumber === mapped.number)
              return true;
            if (entryName && mapped.name && entryName === mapped.name)
              return true;
          }
        }

        const runnerNumber =
          typeof runner === "string" || typeof runner === "number"
            ? normalizeRunnerNumber(runner)
            : extractRunnerNumber(runner);

        if (entryNumber && runnerNumber && entryNumber === runnerNumber) {
          return true;
        }

        const runnerNameCandidate =
          typeof runner === "object"
            ? normalizeRunnerName(
                runner?.name ||
                  runner?.runnerName ||
                  runner?.selectionName ||
                  runner?.horseName ||
                  runner?.horse ||
                  runner?.runner ||
                  runner?.horse?.name ||
                  "",
              )
            : "";

        if (
          entryName &&
          runnerNameCandidate &&
          entryName === runnerNameCandidate
        ) {
          return true;
        }

        return false;
      };

      // Check for scratching first before anything else
      // Check both betfairResults.scratchings and podium array
      if (raceResults?.betfairResults?.scratchings) {
        const scratchings = raceResults.betfairResults.scratchings;
        const isScratched = scratchings?.some((scratching) =>
          runnerMatchesEntry(scratching),
        );

        if (isScratched) {
          console.log(
            "✅ [render-tips] Horse is SCRATCHED (from scratchings array):",
            entry?.name,
          );
          return (
            <span className="text-xs text-gray-500 mt-1 font-bold">SCR</span>
          );
        }
      }

      // Also check the podium arrays for SCRATCHED. Each podium lines up with its own
      // tip list, so match the runner (an Early Mail / Race Day row number would point
      // at a different horse in the Late Mail podium).
      if (raceResults?.podium || raceResults?.earlyPodium) {
        if (isScratchedInPodium(entry, raceResults)) {
          console.log(
            "✅ [render-tips] Horse is SCRATCHED (from podium array):",
            entry?.name,
          );
          return (
            <span className="text-xs text-gray-500 mt-1 font-bold">SCR</span>
          );
        }
      }

      // Check winner
      if (runnerMatchesEntry(raceResults?.betfairResults?.winner)) {
        return <TrophyIcon fill="#ff1493" className="scale-[150%]" />;
      }

      const trimmedPlaceGetters = getTrimmedPlaceGetters(
        raceResults?.betfairResults,
      );

      const isPlaced = trimmedPlaceGetters.some((placeGetter) =>
        runnerMatchesEntry(placeGetter),
      );

      if (isPlaced) {
        return <MedalIcon className="scale-[150%]" />;
      }

      // If we haven't returned by now, the horse was unplaced
      return <span className="text-xs text-gray-500 mt-1 font-bold">UPL</span>;
    } else {
      // This code should never be reached with our updated logic, but keeping as fallback
      return null;
    }
  };

  const getTrimmedPlaceGetters = (data) => {
    const total = data.numberOfRunners;

    let places;
    if (total <= 4) {
      places = 1;
    } else if (total <= 7) {
      places = 2;
    } else {
      places = 3;
    }

    return data.placeGetters?.slice(0, places) ?? [];
  };

  const isTodaysTipsRoute =
    typeof window !== "undefined" &&
    window.location?.pathname?.includes("/todays-tips");

  // Get the race key for the current race
  const raceKey = `${selectedTrack?.replace(/\s+/g, "-")}-${selectedRace}-${today}`;

  const lockedRaceTimeTips = useMemo(() => {
    if (!isLexiSocial || isResultsPage || !isTodaysTipsRoute) return [];
    if (hasRaceResultsForCurrentRace) return [];
    if (skipLeaderboardQuery) return [];

    if (!selectedTrack || !selectedRace || !today) {
      return [];
    }

    const raceData = baseLeaderboardQueryData?.[raceKey];

    if (
      !raceData?.firstFourData ||
      !Array.isArray(raceData.firstFourData) ||
      raceData.firstFourData.length === 0
    ) {
      return [];
    }

    return transformRaceData(
      raceData,
      { includeConfidence: true },
      finalEarlyPredictionsData,
    );
  }, [
    isLexiSocial,
    isResultsPage,
    isTodaysTipsRoute,
    selectedTrack,
    selectedRace,
    today,
    raceKey,
    baseLeaderboardQueryData,
    finalEarlyPredictionsData,
    skipLeaderboardQuery,
    hasRaceResultsForCurrentRace,
  ]);

  const hasLockedRaceTimeTips =
    Array.isArray(lockedRaceTimeTips) && lockedRaceTimeTips.length > 0;

  const lexiEarlyMorningTips = useMemo(() => {
    if (!isLexiSocial || !isTodaysTipsRoute) return [];
    if (!hasRaceResultsForCurrentRace) return [];
    if (!selectedTrack || !selectedRace || !today) return [];

    const raceData = gatedEarlyPredictionsData?.[raceKey];

    if (
      !raceData?.firstFourData ||
      !Array.isArray(raceData.firstFourData) ||
      raceData.firstFourData.length === 0
    ) {
      return [];
    }

    return transformRaceData(
      raceData,
      { includeConfidence: true },
      finalEarlyPredictionsData,
    );
  }, [
    gatedEarlyPredictionsData,
    raceKey,
    isLexiSocial,
    isTodaysTipsRoute,
    hasRaceResultsForCurrentRace,
    selectedTrack,
    selectedRace,
    today,
    finalEarlyPredictionsData,
  ]);

  const hasLexiEarlyMorningTips =
    Array.isArray(lexiEarlyMorningTips) && lexiEarlyMorningTips.length > 0;

  const { regularTipUpdatedAt, earlyTipUpdatedAt } = useMemo(() => {
    const regular =
      finalLeaderboardQueryData?.[raceKey]?.tipUpdatedAt ??
      finalLeaderboardQueryData?.predictionData?.tipUpdatedAt ??
      null;

    const early =
      finalEarlyPredictionsData?.[raceKey]?.tipUpdatedAt ??
      finalLeaderboardQueryData?.earlyPredictionData?.tipUpdatedAt ??
      null;

    return {
      regularTipUpdatedAt: regular,
      earlyTipUpdatedAt: early,
    };
  }, [finalLeaderboardQueryData, finalEarlyPredictionsData, raceKey]);

  const raceTimeTipsDifferFromEarly = useMemo(() => {
    if (!regularTipUpdatedAt || !earlyTipUpdatedAt) return false;

    // Expect "HH:mm" format; compare as minutes since midnight
    const toMinutes = (value) => {
      const [h, m] = `${value}`.split(":").map((v) => parseInt(v, 10));
      if (Number.isNaN(h) || Number.isNaN(m)) return null;
      return h * 60 + m;
    };

    const regularMins = toMinutes(regularTipUpdatedAt);
    const earlyMins = toMinutes(earlyTipUpdatedAt);

    if (regularMins === null || earlyMins === null) return false;

    // Race-time tips are considered present only when the regular tip time is later
    return regularMins > earlyMins;
  }, [regularTipUpdatedAt, earlyTipUpdatedAt]);

  // Broadcast current tip state for debugging banner
  useEffect(() => {
    if (typeof window === "undefined") return;

    const hasRaceTipsReady =
      Array.isArray(leaderboardData) &&
      leaderboardData.length > 0 &&
      raceTimeTipsDifferFromEarly;
    const hasMorningTips = hasLexiEarlyMorningTips;
    const awaitingRaceTimeTips =
      hasLockedRaceTimeTips &&
      raceTimeTipsDifferFromEarly &&
      isTodaysTipsRoute &&
      !hasRaceResultsForCurrentRace &&
      !hasRaceTipsReady;

    let tipsState = "TIPS-M";
    if (hasRaceTipsReady && hasMorningTips) {
      tipsState = "TIPS-BH";
    } else if (hasRaceTipsReady || awaitingRaceTimeTips) {
      tipsState = "TIPS-RT";
    }

    // if (process.env.NODE_ENV === "development") {
    //   console.info("[render-tips] tips-state-dispatch", {
    //     userType: isLexiSocial ? "UT-FREE" : "UT-PAID",
    //     tipsState,
    //     hasRaceTipsReady,
    //     hasMorningTips,
    //     awaitingRaceTimeTips,
    //     hasLockedRaceTimeTips,
    //     raceTimeTipsDifferFromEarly,
    //     leaderboardCount: Array.isArray(leaderboardData)
    //       ? leaderboardData.length
    //       : 0,
    //     isTodaysTipsRoute,
    //     hasRaceResultsForCurrentRace,
    //     regularTipUpdatedAt,
    //     earlyTipUpdatedAt,
    //   });
    // }

    window.dispatchEvent(
      new CustomEvent("lexi:tips-state", {
        detail: {
          userType: isLexiSocial ? "UT-FREE" : "UT-PAID",
          tdm: `TDM-${displayTipsLimit}`,
          tipsState,
          resultsState: hasRaceResultsForCurrentRace
            ? "RESULTS-TRUE"
            : "RESULTS-FALSE",
        },
      }),
    );
  }, [
    isLexiSocial,
    effectiveShowAllTips,
    hasLexiEarlyMorningTips,
    leaderboardData,
    hasRaceResultsForCurrentRace,
    raceTimeTipsDifferFromEarly,
    isTodaysTipsRoute,
    hasLockedRaceTimeTips,
  ]);

  // Get data for both regular and early predictions
  const regularData = finalLeaderboardQueryData?.[raceKey];
  const earlyData = finalEarlyPredictionsData?.[raceKey];

  // Site-wide time format: "1:10 pm" in Sydney time. tipUpdatedAt is a Brisbane
  // "HH:mm" (tips backend); `today` (race date) picks the right Sydney offset.
  const formatTimeWithAMPM = (timeString) =>
    formatTimeOfDay(timeString, { on: today || undefined });

  // Format the times with AM/PM
  const regularTimeFormatted = formatTimeWithAMPM(
    regularData?.tipUpdatedAt
      ? regularData?.tipUpdatedAt
      : finalLeaderboardQueryData?.predictionData?.tipUpdatedAt,
  );
  const earlyTimeFormatted = formatTimeWithAMPM(
    earlyData?.tipUpdatedAt
      ? earlyData?.tipUpdatedAt
      : finalLeaderboardQueryData?.earlyPredictionData?.tipUpdatedAt,
  );
  const earlyMailTimeFormatted = isEarlyMail
    ? regularTimeFormatted || "--"
    : null;

  // Check if the top entry is a winner
  const topEntryIsWinner = useMemo(() => {
    if (
      !leaderboardData ||
      leaderboardData.length === 0 ||
      !finalHistoricalData ||
      !selectedTrack ||
      !selectedRace ||
      !today
    ) {
      return false;
    }

    const topEntry = leaderboardData[0];
    const raceKey = `${selectedTrack.replace(/\s+/g, "-")}-${selectedRace}-${today}`;
    const raceResults = finalHistoricalData[raceKey];

    if (!raceResults?.betfairResults) {
      return false;
    }

    return (
      raceResults?.betfairResults?.winner?.selectionId === topEntry?.selectionId
    );
  }, [
    leaderboardData,
    finalHistoricalData,
    selectedTrack,
    selectedRace,
    today,
  ]);

  // Handle click on share button using localStorage
  const handleShareWinner = () => {
    if (leaderboardData?.length > 0 && topEntryIsWinner) {
      const winnerEntry = {
        ...leaderboardData[0],
        trackName: selectedTrack,
        raceNumber: selectedRace,
      };
      triggerWinnerModal(winnerEntry);
    }
  };

  const showUpdatedIndicator =
    hasEarlyPredictions && raceTimeTipsDifferFromEarly;

  // ---- Tip movement markers ("chevrons") ----------------------------------
  // One rule set for Early Mail -> Race Day tips -> Late Mail (src/utils/tip-movement.js):
  // the top list shows the move since the set before it; older lists show where the horse is now.
  const raceDayTipsForMovement =
    (isLexiSocial ? gatedEarlyPredictionsData : finalEarlyPredictionsData)?.[
      raceKey
    ]?.firstFourData ??
    finalLeaderboardQueryData?.earlyPredictionData?.firstFourData ??
    null;
  const mainTipSetId = isEarlyMail
    ? TIP_SETS.EARLY_MAIL
    : showUpdatedIndicator
      ? TIP_SETS.LATE_MAIL
      : TIP_SETS.RACE_DAY;
  const historicalScratchings =
    finalHistoricalData?.[raceKey]?.betfairResults?.scratchings;

  const tipMovements = useMemo(() => {
    if (hideTipMovement) return null;
    // Lexi Social sees only Race Day tips 1-2 before results: comparing them with
    // Early Mail or Late Mail would leak a locked set, so no markers at all.
    if (isLexiSocial && !hasRaceResultsForCurrentRace) return null;

    let sets;
    if (isEarlyMail) {
      sets = buildTipSets({
        earlyMail: leaderboardData,
        raceDay: tipMovementSources?.raceDay,
        latest: tipMovementSources?.latest,
      });
    } else {
      sets = [];
      const earlyMailTips = tipMovementSources?.earlyMail;
      if (Array.isArray(earlyMailTips) && earlyMailTips.length > 0) {
        sets.push({ id: TIP_SETS.EARLY_MAIL, tips: earlyMailTips });
      }
      // Late Mail only when the top list is labelled Late Mail; then the race-day list sits under it
      if (showUpdatedIndicator && Array.isArray(raceDayTipsForMovement)) {
        sets.push({ id: TIP_SETS.RACE_DAY, tips: raceDayTipsForMovement });
      }
      sets.push({ id: mainTipSetId, tips: leaderboardData });
    }

    return getTipMovements(sets, {
      shownCount: displayTipsLimit,
      scratchings: historicalScratchings,
    });
  }, [
    hideTipMovement,
    isLexiSocial,
    hasRaceResultsForCurrentRace,
    isEarlyMail,
    leaderboardData,
    tipMovementSources,
    showUpdatedIndicator,
    raceDayTipsForMovement,
    mainTipSetId,
    displayTipsLimit,
    historicalScratchings,
  ]);

  // Show the marker column on every list of the race (keeps silks aligned) once there are 2+ sets
  const showMovementColumn = Boolean(tipMovements);
  useEffect(() => {
    if (typeof onTipMovementShownChange !== "function") return undefined;
    onTipMovementShownChange(showMovementColumn);
    return () => onTipMovementShownChange(false);
  }, [onTipMovementShownChange, showMovementColumn]);
  const mainTipMovements = tipMovements?.[mainTipSetId] || null;
  // Marker for a row of the race-day list (Pro "Show race-day tips", or Lexi Social after results)
  const getRaceDayRowMovement = (row, index) => {
    if (!tipMovements) return null;
    if (mainTipSetId !== TIP_SETS.RACE_DAY) {
      return tipMovements[TIP_SETS.RACE_DAY]?.[index] ?? null;
    }
    // No Late Mail: the top list IS the Race Day tips, so the same runner gets the same marker
    const i = leaderboardData.findIndex(
      (top, k) => k < displayTipsLimit && isSameRunner(top, row),
    );
    return i === -1 ? null : (mainTipMovements?.[i] ?? null);
  };
  // -------------------------------------------------------------------------

  // ---- Prices: "$ price" / "odds %" toggle and "Odds NN%" per list ----------
  // (src/utils/tip-odds.js) Only tips the viewer can see count: the shown 2 or 4,
  // never Lexi Social's locked rows; scratched runners and missing prices skipped.
  const raceResultsForOdds = finalHistoricalData?.[raceKey] || null;
  const raceTipsForScratch = [
    ...(Array.isArray(leaderboardData) ? leaderboardData : []),
    ...(Array.isArray(raceDayTipsForMovement) ? raceDayTipsForMovement : []),
    ...(tipMovementSources?.raceDay?.firstFourData || []),
    ...(tipMovementSources?.latest?.firstFourData || []),
  ];
  const lockedFromIndex =
    isLexiSocial && !hasRaceResultsForCurrentRace ? minTips : Infinity;
  const getListOddsTotal = (rows, lockFrom = Infinity) =>
    getOddsTotal(
      (Array.isArray(rows) ? rows : []).map((row, index) => ({
        price: pickTipPrice(row),
        locked: index >= lockFrom,
        scratched: isRunnerScratched(
          row,
          raceResultsForOdds,
          raceTipsForScratch,
        ),
      })),
      { shownCount: displayTipsLimit },
    );
  const listHasPrices = (rows, lockFrom = Infinity) =>
    (Array.isArray(rows) ? rows : [])
      .slice(0, Math.min(displayTipsLimit, lockFrom))
      .some((row) => pickTipPrice(row) !== null);
  const raceDayHistoryTips = (
    finalEarlyPredictionsData?.[raceKey] ||
    finalLeaderboardQueryData?.earlyPredictionData
  )?.firstFourData;
  const mainOddsTotal = getListOddsTotal(leaderboardData, lockedFromIndex);
  const socialRaceDayOddsTotal = getListOddsTotal(
    lexiEarlyMorningTips,
    lockedFromIndex,
  );
  const raceDayHistoryOddsTotal = getListOddsTotal(raceDayHistoryTips);
  const renderOddsTotal = (total) =>
    total ? (
      <span
        className="tip-odds-total normal-case whitespace-nowrap"
        title={ODDS_TOTAL_HINT}
        data-testid="tip-odds-total"
      >
        {" · "}
        {formatOddsTotal(total)}
        <span className="sr-only"> ({ODDS_TOTAL_HINT})</span>
      </span>
    ) : null;
  const renderPriceToggle = (rows, lockFrom = Infinity) =>
    showPriceToggle && listHasPrices(rows, lockFrom) ? (
      <TipPriceToggle
        source={isEarlyMail ? "lexi" : "market"}
        mode={priceDisplayMode}
        onToggle={togglePriceOddsMode}
      />
    ) : null;
  // -------------------------------------------------------------------------

  return (
    <>
      <div className="time-gen-run-container mb-3">
        <div className="generated-time-container pl-[0.4rem] w-full min-h-[1em]">
          {(isEarlyMail || regularTimeFormatted) && (
            <div className="flex items-center justify-start text-xxs text-brand-pink">
              <div className="flex items-center">
                {showUpdatedIndicator ? (
                  <BellIcon className="w-3 h-3 mr-1 transform translate-y-[-1px] animate-bell-ring" />
                ) : (
                  <Clock className="w-3 h-3 mr-1" />
                )}

                <span className="uppercase">
                  {isEarlyMail ? (
                    <>
                      <b>Early Mail</b> arrived{" "}
                      <span className="normal-case">{earlyMailTimeFormatted}</span>
                      {earlyMailRelativeLabel
                        ? ` ${earlyMailRelativeLabel}`
                        : ""}
                    </>
                  ) : showUpdatedIndicator ? (
                    <>
                      <b>Late Mail</b> updated at{" "}
                      <span className="normal-case">{regularTimeFormatted}</span>
                    </>
                  ) : (
                    <>
                      <b>Race-day tips</b> generated at{" "}
                      <span className="normal-case">{regularTimeFormatted}</span>
                    </>
                  )}
                  {renderOddsTotal(mainOddsTotal)}
                </span>
              </div>
              {/* Share button moved here */}
              {!isEarlyMail &&
                topEntryIsWinner &&
                leaderboardData?.length > 0 && (
                  <button
                    className="text-xs z-10 bg-brand-pink text-white rounded-lg py-1 px-2 shadow-md hover:bg-pink-600 transition-colors ml-2"
                    onClick={handleShareWinner}
                    aria-label="Share winner"
                  >
                    SHARE
                  </button>
                )}
            </div>
          )}
        </div>

        {/* Race Time Display - Show for all races with market start time */}
        {(() => {
          // Get race key and current race data (supports both keyed and single-object shapes)
          const raceKey = `${selectedTrack?.replace(/\s+/g, "-")}-${selectedRace}-${today}`;
          const raceData =
            finalLeaderboardQueryData?.[raceKey] ||
            finalLeaderboardQueryData?.predictionData ||
            finalLeaderboardQueryData?.earlyPredictionData ||
            finalEarlyPredictionsData?.[raceKey] ||
            finalEarlyPredictionsData?.predictionData ||
            finalEarlyPredictionsData?.earlyPredictionData ||
            null;

          const marketStartTime =
            raceData?.marketStartTime ||
            raceData?.market_start_time ||
            raceData?.predictionData?.marketStartTime ||
            raceData?.predictionData?.market_start_time ||
            raceData?.earlyPredictionData?.marketStartTime ||
            raceData?.earlyPredictionData?.market_start_time ||
            finalLeaderboardQueryData?.marketStartTime ||
            finalLeaderboardQueryData?.market_start_time ||
            finalEarlyPredictionsData?.[raceKey]?.marketStartTime ||
            finalEarlyPredictionsData?.[raceKey]?.market_start_time ||
            null;

          if (!marketStartTime) return null;

          // Use the persistent favorite status from component state
          const isFavorited = favoriteStatus[raceKey] || false;

          return (
            <div className="race-start-time-container relative text-brand-pink text-xxs uppercase px-0 py-0">
              <div className="flex justify-between items-center">
                <div className="race-time w-auto text-right">
                  <div className="h-6 flex items-center justify-end text-nowrap">
                    <MarketStartTime
                      marketStartTime={marketStartTime}
                      hasHistoricalData={internalHasHistoricalData}
                      variant="tips"
                      hasRaceResults={hasRaceResults(
                        selectedTrack,
                        selectedRace,
                        today,
                        finalHistoricalData,
                      )}
                    />
                  </div>
                </div>

                {/* Add favorite button */}
                <button
                  onClick={() => toggleFavorite(raceKey)}
                  className="add-fav-button ml-2 focus:outline-none"
                  aria-label={
                    isFavorited ? "Remove from My Tips" : "Add to My Tips"
                  }
                  data-gtm-element-id="todays-tips_favorite_button-1"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill={isFavorited ? "deeppink" : "none"}
                    stroke={isFavorited ? "deeppink" : "grey"}
                    strokeWidth="2"
                    className="w-4 h-4 transition-colors scale-[150%]"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"
                    />
                  </svg>
                </button>
              </div>
            </div>
          );
        })()}
      </div>

      {raceCard && <RaceInfoPanel card={raceCard} />}

      {!Array.isArray(leaderboardData) || leaderboardData?.length === 0 ? (
        <div className="flex flex-col items-center justify-center p-4 bg-white rounded-lg shadow-sm">
          <AlertTriangle className="w-8 h-8 text-yellow-500 mb-2" />
          <p className="text-gray-700 font-medium">No race data available</p>
          <p className="text-sm text-gray-500">
            {isResultsPage
              ? "Results will be published once the race is complete."
              : "Please check back later for predictions."}
          </p>
        </div>
      ) : null}

      <div className={cn(showExtraDetails ? "divide-y divide-gray-100" : "")}>
        {leaderboardData?.length > 0 &&
          leaderboardData?.map((entry, index) => {
            // Skip invalid entries
            if (!entry || !entry.name || !entry.number) {
              return null;
            }

            // Only show first X entries based on user preference
            if (index >= displayTipsLimit) {
              return null;
            }

            // Blur tips beyond minTips ONLY for Lexi Social (free tier) users
            // All paid products (Lexi Pro, 7-Day Pass, etc.) see all tips clearly
            const shouldBlur =
              isLexiSocial &&
              effectiveShowAllTips &&
              index >= minTips &&
              !hasRaceResultsForCurrentRace;
            const blurClasses = shouldBlur
              ? "opacity-60 blur-[5px] grayscale select-none pointer-events-none"
              : "";

            return (
              <div
                key={`tip-${index}-${entry?.number || "unknown"}-${entry?.name || "unnamed"}`}
                className={`tip-item-container mb-4 px-0 pb-0 transition-all duration-500 mt-1 relative`}
              >
                <div
                  className={`tip-item-container-inner flex justify-between items-center gap-1 w-full ${blurClasses}`}
                >
                  <div className="tip-item-left-content flex items-center gap-2 xxs:gap-1 flex-1 min-w-0">
                    {showMovementColumn && (
                      <TipMovementMarker
                        movement={
                          shouldBlur ? null : mainTipMovements?.[index]
                        }
                      />
                    )}

                    <div className="silks-container w-6 h-6 xs:w-7 xs:h-7 flex-shrink-0">
                      {(entry?.horseId || entry?.silks) &&
                        (() => {
                          const resultDisplay = getResultDisplay(
                            entry,
                            true,
                            finalEarlyPredictionsData,
                          );
                          const hasResultDisplay = !!resultDisplay;
                          const shouldUseFallbackSilk =
                            isLexiSocial &&
                            index >= minTips &&
                            !hasResultDisplay;

                          const silkSrc = shouldUseFallbackSilk
                            ? "/images/silk-fallback.jpg"
                            : entry?.horseId
                              ? `/api/images/silks?filename=${entry.horseId}.jpeg`
                              : `https://www.puntingform.com.au/images/jockeysilks/${entry.horseId}.jpeg`;

                          return (
                            <img
                              src={silkSrc}
                              alt={"Jockey silk"}
                              className="w-6 h-6 xs:w-7 xs:h-7 object-contain"
                              onError={(e) => {
                                // If the new API failed and we have a fallback URL, try it
                                if (
                                  entry?.horseId &&
                                  entry?.silks &&
                                  e.target.src.includes("/api/images/silks")
                                ) {
                                  e.target.src = `https://www.puntingform.com.au/images/jockeysilks/${entry.horseId}.jpeg`;
                                } else if (
                                  !hasResultDisplay &&
                                  !e.target.src.includes("silk-fallback.jpg")
                                ) {
                                  // If both failed and results aren't in, use the fallback image
                                  e.target.src = "/images/silk-fallback.jpg";
                                }
                              }}
                            />
                          );
                        })()}
                    </div>

                    <div className="tip-item-number-container flex-shrink-0 dog">
                      <span className="font-semibold text-sm">
                        {(() => {
                          const resultDisplay = getResultDisplay(
                            entry,
                            true,
                            finalEarlyPredictionsData,
                          );

                          // Show placeholder ONLY for Lexi Social (free tier) users on locked tips
                          // All paid products (Lexi Pro, 7-Day Pass, etc.) see real horse numbers
                          return resultDisplay
                            ? `${entry?.number}. `
                            : isLexiSocial && index >= minTips
                              ? "-"
                              : `${entry?.number}. `;
                        })()}
                      </span>
                    </div>

                    <div className="tip-item-name-container min-w-0 flex-1">
                      <span
                        className="font-semibold truncate text-sm block"
                        title={
                          isLexiSocial && index >= minTips
                            ? "Upgrade to unlock"
                            : entry?.name
                        }
                      >
                        {(() => {
                          const resultDisplay = getResultDisplay(
                            entry,
                            true,
                            finalEarlyPredictionsData,
                          );

                          // Show "Locked runner" ONLY for Lexi Social (free tier) users on locked tips
                          // All paid products (Lexi Pro, 7-Day Pass, etc.) see real horse names
                          return resultDisplay
                            ? entry?.name
                            : isLexiSocial && index >= minTips
                              ? "Locked runner"
                              : entry?.name;
                        })()}
                      </span>
                    </div>
                  </div>

                  <div className="tip-item-right-content flex items-center gap-3 xxs:gap-1 flex-shrink-0">
                    <div className="tip-item-right-content-inner flex items-center gap-3 xxs:gap-1 pr-1">
                      {(() => {
                        const resultDisplay = getResultDisplay(
                          entry,
                          true,
                          finalEarlyPredictionsData,
                        );

                        // Find matching entry in early predictions to compare prices
                        const raceKey = `${selectedTrack?.replace(/\s+/g, "-")}-${selectedRace}-${today}`;
                        const earlyPredictionsForRace =
                          finalEarlyPredictionsData?.[raceKey]?.firstFourData ||
                          finalLeaderboardQueryData?.earlyPredictionData
                            ?.firstFourData;

                        // Find matching early entry by selectionId
                        let earlyEntry = null;
                        if (
                          Array.isArray(earlyPredictionsForRace) &&
                          entry?.selectionId
                        ) {
                          earlyEntry = earlyPredictionsForRace.find(
                            (e) => e?.selectionId === entry.selectionId,
                          );
                        }
                        // Get comparison data if we have early odds
                        const earlyPrice = entry?.currentPrice;
                        const earlyEffectivePrice =
                          entry?.effectivePrice &&
                          earlyEntry?.effectivePrice !== "N/A" &&
                          !isNaN(parseFloat(earlyEntry?.effectivePrice))
                            ? earlyEntry?.effectivePrice
                            : earlyEntry?.lastPriceTraded;
                        const currentPrice = entry?.currentPrice;
                        const currentEffectivePrice =
                          entry?.effectivePrice &&
                          entry?.effectivePrice !== "N/A" &&
                          !isNaN(parseFloat(entry?.effectivePrice))
                            ? entry?.effectivePrice
                            : entry?.lastPriceTraded;
                        const oddsComparison = getOddsComparison(
                          currentPrice,
                          earlyPrice,
                        );

                        return (
                          <>
                            {/* Show flame icon for top Tip when odds are over the hotTipMinPrice threshold */}
                            {index === 0 &&
                              currentPrice &&
                              !isHotTipSettingsLoading &&
                              hotTipMinPrice > 0 &&
                              parseFloat(currentPrice) > hotTipMinPrice && (
                                <div className="top-pick-container flex flex-row items-center ml-1 bg-transparent xsm:bg-[#FFEBBB] rounded-full px-2 xxs:px-1.5 py-1 text-nowrap">
                                  <FlameIcon className="w-4 h-4 xxs:w-3 xxs:h-3" />
                                  <span className="hidden xsm:inline-block text-[10px] text-[#A0200C] font-bold">
                                    {(() => {
                                      // Check if this entry is a winner
                                      if (
                                        !finalHistoricalData ||
                                        !selectedTrack ||
                                        !selectedRace ||
                                        !today
                                      ) {
                                        return "Top Tip";
                                      }

                                      const raceKey = `${selectedTrack.replace(/\s+/g, "-")}-${selectedRace}-${today}`;
                                      const raceResults =
                                        finalHistoricalData[raceKey];

                                      if (!raceResults?.betfairResults) {
                                        return "Top Tip";
                                      }

                                      const isWinner =
                                        raceResults.betfairResults.winner
                                          ?.selectionId === entry?.selectionId;

                                      return isWinner ? "Hot Tip" : "Top Tip";
                                    })()}
                                  </span>
                                </div>
                              )}
                            {/* Display odds price with comparison styling if available
                              Show placeholder ONLY for Lexi Social (free tier) users on locked tips
                              All paid products (Lexi Pro, 7-Day Pass, etc.) see real odds */}
                            <div
                              className={`tip-odds-container text-right min-w-[4.5rem] flex justify-end ${!resultDisplay ? "ml-auto" : ""}`}
                            >
                              {isLexiSocial &&
                              index >= minTips &&
                              !hasRaceResultsForCurrentRace ? (
                                <span className="text-xs font-medium">
                                  $XX.XX
                                </span>
                              ) : (
                                (() => {
                                  // Same price the "Odds NN%" total uses
                                  const numericPrice = pickTipPrice(entry);

                                  // Early Mail: show pill + allow toggling to odds%
                                  if (isEarlyMail) {
                                    return (
                                      <button
                                        type="button"
                                        onClick={togglePriceOddsMode}
                                        className="inline-flex items-center justify-center rounded-full bg-white border border-gray-200 shadow-[1px_1px_1px_0px_rgba(0,0,0,0.12)] px-2 py-0.5 text-xs font-semibold text-gray-800 whitespace-nowrap cursor-pointer"
                                        aria-label="Toggle price/odds display"
                                      >
                                        {formatPriceOrOddsPercent(numericPrice)}
                                      </button>
                                    );
                                  }

                                  // Default (non-early-mail): keep existing comparison styling
                                  if (oddsComparison) {
                                    return (
                                      <div className="flex items-center gap-1">
                                        <span
                                          className="text-xs font-medium px-1 xxs:px-2 py-1 rounded-md text-nowrap"
                                          style={{
                                            backgroundColor:
                                              oddsComparison.backgroundColor,
                                            color: oddsComparison.textColor,
                                          }}
                                        >
                                          {oddsComparison.icon ===
                                          "trending-down" ? (
                                            <TrendingDownIcon className="inline-block w-3 h-3 mr-1 scale-x-[-1] rotate-180" />
                                          ) : (
                                            <MovingUpIcon className="inline-block w-3 h-3 mr-1 scale-x-[-1] rotate-180" />
                                          )}
                                          {formatPriceOrOddsPercent(
                                            numericPrice,
                                          )}
                                        </span>
                                      </div>
                                    );
                                  }

                                  return (
                                    <span className="text-xs font-medium whitespace-nowrap">
                                      {formatPriceOrOddsPercent(numericPrice)}
                                    </span>
                                  );
                                })()
                              )}
                            </div>

                            {/* Only show result container if there's a result */}
                            {resultDisplay && (
                              <div className="tip-result-container flex items-center justify-center min-w-[2rem] pl-2">
                                {resultDisplay}
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>

                {/* Extra details section - conditionally shown */}
                {showExtraDetails && <HorseExtraDetails entry={entry} />}

                {isEarlyMail &&
                  showEarlyMailReview &&
                  earlyMailAssessmentHtml &&
                  index === Math.min(3, displayTipsLimit - 1) && (
                    <div className="mt-2 rounded-lg bg-white border border-gray-200 shadow-[1px_1px_1px_0px_rgba(0,0,0,0.12)] px-3 py-2">
                      <div
                        className="text-xs text-gray-700 leading-[1.6]"
                        dangerouslySetInnerHTML={{
                          __html: earlyMailAssessmentHtml,
                        }}
                      />
                    </div>
                  )}
              </div>
            );
          })}
      </div>

      {renderPriceToggle(leaderboardData, lockedFromIndex)}

      {hasLexiEarlyMorningTips && (
        <>
          {Array.isArray(leaderboardData) && leaderboardData.length > 0 && (
            <div className="border-t border-dashed border-gray-300 my-4" />
          )}

          <div className="mb-4">
            <div className="flex items-center justify-between px-1 text-xxs uppercase text-gray-500">
              <div className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                <span>
                  <b>Race-day tips</b> generated at{" "}
                  <span className="normal-case">{earlyTimeFormatted || "--"}</span>
                  {renderOddsTotal(socialRaceDayOddsTotal)}
                </span>
              </div>
            </div>

            <div className="mt-2 space-y-3">
              {lexiEarlyMorningTips
                .filter((_, idx) => idx < displayTipsLimit)
                .map((entry, index) => {
                  const resultDisplay = getResultDisplay(
                    entry,
                    true,
                    finalEarlyPredictionsData,
                    true,
                  );

                  // Blur early morning tips beyond minTips ONLY for Lexi Social (free tier) users
                  // All paid products (Lexi Pro, 7-Day Pass, etc.) see all tips clearly
                  const shouldBlur =
                    isLexiSocial &&
                    effectiveShowAllTips &&
                    index >= minTips &&
                    !hasRaceResultsForCurrentRace;
                  const blurClasses = shouldBlur
                    ? "opacity-60 blur-[5px] select-none pointer-events-none"
                    : "";

                  const displayedOdds = formatPriceOrOddsPercent(
                    pickTipPrice(entry),
                  );

                  return (
                    <div
                      key={`lexi-early-tip-${entry?.selectionId || index}`}
                      className="tip-item-container mb-4 px-0 pb-0 transition-all duration-500 mt-1 relative"
                    >
                      <div
                        className={`tip-item-container-inner flex justify-between items-center gap-1 w-full ${blurClasses}`}
                      >
                        <div className="tip-item-left-content flex items-center gap-2 xxs:gap-1 flex-1 min-w-0">
                          {showMovementColumn && (
                            <TipMovementMarker
                              movement={
                                shouldBlur
                                  ? null
                                  : getRaceDayRowMovement(entry, index)
                              }
                            />
                          )}

                          <div className="silks-container w-6 h-6 xs:w-7 xs:h-7 flex-shrink-0">
                            {(entry?.horseId || entry?.silks) && (
                              <img
                                src={
                                  entry?.horseId
                                    ? `/api/images/silks?filename=${entry.horseId}.jpeg`
                                    : `https://www.puntingform.com.au/images/jockeysilks/${entry.horseId}.jpeg`
                                }
                                alt={"Jockey silk"}
                                className="w-6 h-6 xs:w-7 xs:h-7 object-contain"
                                onError={(e) => {
                                  if (
                                    entry?.horseId &&
                                    entry?.silks &&
                                    e.target.src.includes("/api/images/silks")
                                  ) {
                                    e.target.src = `https://www.puntingform.com.au/images/jockeysilks/${entry.horseId}.jpeg`;
                                  } else if (
                                    !e.target.src.includes("silk-fallback.jpg")
                                  ) {
                                    e.target.src = "/images/silk-fallback.jpg";
                                  }
                                }}
                              />
                            )}
                          </div>

                          <div className="tip-item-number-container flex-shrink-0">
                            <span className="font-semibold text-sm">
                              {entry?.number ? `${entry.number}. ` : ""}
                            </span>
                          </div>

                          <div className="tip-item-name-container min-w-0 flex-1">
                            <span
                              className="font-semibold truncate text-sm block"
                              title={
                                isLexiSocial && index >= minTips
                                  ? "Upgrade to unlock"
                                  : entry?.name
                              }
                            >
                              {(() => {
                                const resultDisplay = getResultDisplay(
                                  entry,
                                  true,
                                  finalEarlyPredictionsData,
                                );
                                return resultDisplay
                                  ? entry?.name
                                  : isLexiSocial && index >= minTips
                                    ? "Locked runner"
                                    : entry?.name;
                              })()}
                            </span>
                          </div>
                        </div>

                        <div className="tip-item-right-content flex items-center gap-3 xxs:gap-1 flex-shrink-0">
                          <div className="tip-item-right-content-inner flex items-center gap-3 xxs:gap-1 pr-1">
                            <div className="tip-odds-container text-right min-w-[4.5rem] flex justify-end ml-auto">
                              <span className="text-xs font-medium whitespace-nowrap">
                                {displayedOdds}
                              </span>
                            </div>

                            {resultDisplay && (
                              <div className="tip-result-container flex items-center justify-center min-w-[2rem] pl-2">
                                {resultDisplay}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
            {renderPriceToggle(lexiEarlyMorningTips, lockedFromIndex)}
          </div>
        </>
      )}

      {/* Locked race time tips preview - Only shown to paid users (Lexi Pro, 7-Day Pass, etc.)
          Lexi Social users don't see this section at all */}
      {!isLexiSocial &&
        hasLockedRaceTimeTips &&
        !hasRaceResultsForCurrentRace && (
          <div className="mt-3 space-y-3">
            {lockedRaceTimeTips
              .filter((_, idx) => idx < displayTipsLimit)
              .map((entry, index) => {
                const shouldBlur = true;
                const resultDisplay = getResultDisplay(
                  entry,
                  true,
                  finalEarlyPredictionsData,
                );

                const displayedOdds = (() => {
                  const priceOptions = [
                    entry?.effectivePrice,
                    entry?.currentPrice,
                    entry?.lastPriceTraded,
                  ].filter(
                    (price) =>
                      price !== undefined &&
                      price !== null &&
                      price !== "N/A" &&
                      !Number.isNaN(parseFloat(price)),
                  );

                  if (priceOptions.length === 0) return "-";
                  const numeric = parseFloat(priceOptions[0]);
                  return Number.isFinite(numeric)
                    ? `$${numeric.toFixed(2)}`
                    : "-";
                })();

                return (
                  <div
                    key={`locked-tip-${entry?.selectionId || index}`}
                    className="tip-item-container mb-3 px-0 pb-0 transition-all duration-500 mt-1"
                  >
                    <div className="tip-item-container-inner flex justify-between items-center gap-1 w-full opacity-80">
                      <div className="tip-item-left-content flex items-center gap-2 xxs:gap-1 flex-1 min-w-0">
                        <div className="silks-container w-6 h-6 xs:w-7 xs:h-7 flex-shrink-0">
                          <div
                            className="w-6 h-6 xs:w-7 xs:h-7 rounded bg-gradient-to-br from-gray-200 via-gray-300 to-gray-400 blur-[5px] grayscale"
                            aria-hidden="true"
                          />
                        </div>

                        <div className="tip-item-number-container flex-shrink-0">
                          <span
                            className="font-semibold text-sm blur-sm select-none pointer-events-none"
                            aria-hidden="true"
                          >
                            {entry?.number ? `${entry.number}.` : "-"}
                          </span>
                          <span className="sr-only">
                            Race time tip locked for Lexi Social
                          </span>
                        </div>

                        <div className="tip-item-name-container min-w-0 flex-1">
                          <span
                            className="font-semibold truncate text-sm block blur-sm select-none pointer-events-none"
                            aria-hidden="true"
                            title="Upgrade to unlock"
                          >
                            {entry?.name || "Locked runner"}
                          </span>
                        </div>
                      </div>

                      <div className="tip-item-right-content flex items-center gap-3 xxs:gap-1 flex-shrink-0">
                        <div className="tip-item-right-content-inner flex items-center gap-3 xxs:gap-1 pr-1">
                          <div
                            className="tip-odds-container text-right blur-sm select-none pointer-events-none min-w-[3rem]"
                            aria-hidden="true"
                          >
                            <span className="text-xs font-medium">
                              {displayedOdds}
                            </span>
                          </div>

                          {resultDisplay && (
                            <div className="tip-result-container flex items-center justify-center min-w-[2rem] pl-2">
                              {resultDisplay}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        )}

      {/* Tip history toggle - Only shown to paid users (Lexi Pro, 7-Day Pass, etc.)
          Lexi Social users don't have access to tip history */}
      {hasEarlyPredictions && !isLexiSocial && (
        <div className="flex flex-col gap-2 mb-2">
          {/* Tip history button */}
          <div className="tip-history-button-container flex justify-between items-center">
            <button
              className="text-sm flex ml-auto items-center gap-2"
              onClick={toggleTipHistory}
              data-gtm-element-id="todays-tips_history_button-1"
            >
              <span className="text-xxs uppercase text-[#ABABAB]">
                {isTipHistoryExpanded
                  ? "Hide race-day tips"
                  : "Show race-day tips"}
              </span>
              <ChevronDownIcon
                className={`w-3 h-3 text-[#ABABAB] transform transition-transform duration-300 ${isTipHistoryExpanded ? "rotate-180" : ""}`}
              />
            </button>
          </div>
        </div>
      )}

      {/* Only show upgrade prompt for Lexi Social (free tier) users
          All paid products (Lexi Pro, 7-Day Pass, etc.) have full access */}
      {isLexiSocial && (
        <div className="mt-2 mb-4 px-3 py-3 rounded-lg border border-pink-200 bg-pink-50 text-sm text-pink-900 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Lock className="w-6 h-6 text-pink-700" aria-hidden="true" />
            <span className="font-semibold">
              Upgrade to see race time tips <br /> and exotics <em>before</em>{" "}
              the jump!
            </span>
          </div>
          <button
            className="text-xs font-semibold text-white bg-[#F30099] hover:bg-pink-700 rounded-md px-3 py-2 transition-colors"
            onClick={triggerUpgradeFlow}
          >
            Upgrade
          </button>
        </div>
      )}

      {hasEarlyPredictions &&
        isTipHistoryExpanded &&
        (() => {
          try {
            const raceKey = `${selectedTrack?.replace(/\s+/g, "-")}-${selectedRace}-${today}`;

            const earlyData = finalEarlyPredictionsData?.[raceKey]
              ? finalEarlyPredictionsData?.[raceKey]
              : finalLeaderboardQueryData?.earlyPredictionData;

            if (!earlyData) {
              return (
                <div className="early-prediction-data-container mt-4 mb-2 p-3 bg-gray-50 rounded-lg border border-pink-200">
                  <p className="text-sm text-center text-gray-500">
                    No early prediction data found for this race
                  </p>
                </div>
              );
            }

            const firstFourData = earlyData?.firstFourData;
            if (!Array.isArray(firstFourData) || firstFourData?.length === 0) {
              return (
                <div className="early-prediction-data-container mt-4 mb-2 p-3 bg-gray-50 rounded-lg border border-pink-200">
                  <p className="text-sm text-center text-gray-500">
                    Early prediction data is not available in the correct format
                  </p>
                </div>
              );
            }

            return (
              <div className="early-prediction-data-container border-t border-dashed border-gray-400 pt-4">
                {earlyData?.tipUpdatedAt && (
                  <div className="generated-time-container mb-3 min-h-[1em]">
                    <div className="flex items-center justify-start text-xxs text-gray-500">
                      <Clock className="w-3 h-3 mr-1" />
                      <span className="uppercase">
                        <b>Race-day tips</b> generated at{" "}
                        <span className="normal-case">{earlyTimeFormatted}</span>
                        {renderOddsTotal(raceDayHistoryOddsTotal)}
                      </span>
                    </div>
                  </div>
                )}

                {firstFourData?.map((entry, index) => {
                  if (index >= displayTipsLimit) return null;

                  const formattedName = entry?.formattedName || "";
                  const parts = formattedName?.split(".");
                  const horseNumber = parts?.[0] || "#";
                  const horseName =
                    parts?.length > 1
                      ? parts.slice(1).join(" ").trim()
                      : "Unknown Horse";

                  const formattedEntry = {
                    lastPriceTraded: entry?.lastPriceTraded,
                    rank: index + 1,
                    number: horseNumber,
                    name: horseName,
                    currentPrice: entry?.currentPrice,
                    effectivePrice: entry?.effectivePrice,
                    selectionId: entry?.selectionId,
                    horseId: entry?.horseId, // Add horseId for early predictions
                  };

                  return (
                    <div
                      key={`early-tip-${index}`}
                      className="tip-item-container mb-4 px-0 pb-0 transition-all duration-500 mt-1"
                    >
                      <div className="tip-item-container-inner flex justify-between items-center gap-1 w-full">
                        <div className="tip-item-left-content flex items-center gap-2 xxs:gap-1 flex-1 min-w-0">
                          {showMovementColumn && (
                            <TipMovementMarker
                              movement={getRaceDayRowMovement(entry, index)}
                            />
                          )}

                          <div className="silks-container w-6 h-6 xs:w-7 xs:h-7 flex-shrink-0">
                            {(entry?.horseId || entry?.silks) && (
                              <img
                                // https://www.puntingform.com.au/images/jockeysilks/1121436.jpeg
                                src={
                                  entry?.horseId
                                    ? `/api/images/silks?filename=${entry.horseId}.jpeg`
                                    : `https://www.puntingform.com.au/images/jockeysilks/${entry.horseId}.jpeg`
                                }
                                alt={"Jockey silk"}
                                className="w-6 h-6 xs:w-7 xs:h-7 object-contain"
                                onError={(e) => {
                                  // If the new API failed and we have a fallback URL, try it
                                  if (
                                    entry?.horseId &&
                                    entry?.silks &&
                                    e.target.src.includes("/api/images/silks")
                                  ) {
                                    e.target.src = `https://www.puntingform.com.au/images/jockeysilks/${entry.horseId}.jpeg`;
                                  } else if (
                                    !e.target.src.includes("silk-fallback.jpg")
                                  ) {
                                    // If both failed, use the fallback image
                                    e.target.src = "/images/silk-fallback.jpg";
                                  }
                                }}
                              />
                            )}
                          </div>

                          <div className="tip-item-number-container flex-shrink-0">
                            <span className="font-semibold text-sm">
                              {horseNumber}.{" "}
                            </span>
                          </div>

                          <div className="tip-item-name-container min-w-0 flex-1">
                            <span
                              className="font-semibold truncate text-sm block"
                              title={horseName}
                            >
                              {horseName}
                            </span>
                          </div>
                        </div>

                        <div className="tip-item-right-content flex items-center gap-3 xxs:gap-1 flex-shrink-0">
                          <div className="tip-item-right-content-inner flex items-center gap-3 xxs:gap-1 pr-1">
                            {(() => {
                              const resultDisplay = getResultDisplay(
                                formattedEntry,
                                true,
                                finalEarlyPredictionsData,
                                !internalHasHistoricalData,
                              );

                              // Find matching entry in current predictions to compare prices
                              const currentPredictions =
                                finalLeaderboardQueryData?.predictionData
                                  ?.firstFourData;

                              // Find matching current entry by selectionId
                              let currentEntry = null;
                              if (
                                Array.isArray(leaderboardData) &&
                                formattedEntry?.selectionId
                              ) {
                                currentEntry = leaderboardData.find(
                                  (e) =>
                                    e?.selectionId ===
                                    formattedEntry.selectionId,
                                );
                              }

                              // Get comparison data if we have current odds
                              const earlyPrice = formattedEntry?.currentPrice;
                              const earlyEffectivePrice =
                                formattedEntry?.effectivePrice &&
                                formattedEntry?.effectivePrice !== "N/A" &&
                                !isNaN(
                                  parseFloat(formattedEntry?.effectivePrice),
                                )
                                  ? formattedEntry?.effectivePrice
                                  : formattedEntry?.lastPriceTraded;
                              const currentPrice = formattedEntry?.currentPrice;
                              const earlyCurrentEffectivePrice =
                                formattedEntry?.effectivePrice &&
                                formattedEntry?.effectivePrice !== "N/A" &&
                                !isNaN(
                                  parseFloat(formattedEntry?.effectivePrice),
                                )
                                  ? formattedEntry?.effectivePrice
                                  : formattedEntry?.lastPriceTraded;
                              const oddsComparison = getOddsComparison(
                                currentPrice,
                                earlyPrice,
                              );

                              return (
                                <>
                                  {/* Show flame icon for top Tip when odds are over the hotTipMinPrice threshold */}
                                  {index === 0 &&
                                    currentPrice &&
                                    !isHotTipSettingsLoading &&
                                    hotTipMinPrice > 0 &&
                                    parseFloat(currentPrice) >
                                      hotTipMinPrice && (
                                      <div className="top-pick-container flex flex-row items-center ml-1 bg-[#FFEBBB] rounded-full px-2 xxs:px-1.5 py-1 text-nowrap">
                                        <FlameIcon className="w-4 h-4 xxs:w-3 xxs:h-3" />
                                        <span className="hidden xsm:inline-block text-[10px] text-[#A0200C] font-bold">
                                          {(() => {
                                            // Check if this entry is a winner
                                            if (
                                              !finalHistoricalData ||
                                              !selectedTrack ||
                                              !selectedRace ||
                                              !today
                                            ) {
                                              return "Top Tip";
                                            }
                                            const raceKey = `${selectedTrack.replace(/\s+/g, "-")}-${selectedRace}-${today}`;
                                            const raceResults =
                                              finalHistoricalData[raceKey];
                                            if (!raceResults?.betfairResults) {
                                              return "Top Tip";
                                            }
                                            const isWinner =
                                              raceResults.betfairResults.winner
                                                ?.selectionId ===
                                              formattedEntry?.selectionId;
                                            return isWinner
                                              ? "Hot Tip"
                                              : "Top Tip";
                                          })()}
                                        </span>
                                      </div>
                                    )}
                                  {/* Display odds price with comparison styling if available */}
                                  <div
                                    className={`tip-odds-container text-right min-w-[4.5rem] flex justify-end ${!resultDisplay ? "ml-auto" : ""}`}
                                  >
                                    {oddsComparison ? (
                                      <div className="flex items-center gap-1">
                                        <span
                                          className="text-xs font-medium px-1 xxs:px-2 py-1 rounded-md text-nowrap"
                                          style={{
                                            backgroundColor:
                                              oddsComparison.backgroundColor,
                                            color: oddsComparison.textColor,
                                          }}
                                        >
                                          {oddsComparison.icon ===
                                          "trending-down" ? (
                                            <TrendingDownIcon className="inline-block w-3 h-3 mr-1 scale-x-[-1] rotate-180" />
                                          ) : (
                                            <MovingUpIcon className="inline-block w-3 h-3 mr-1 scale-x-[-1] rotate-180" />
                                          )}
                                          {formatPriceOrOddsPercent(
                                            pickTipPrice(formattedEntry),
                                          )}
                                        </span>
                                        {/* Warning icon for low confidence */}
                                        {entry?.effectivePriceConfidence ===
                                          "LOW" && (
                                          <AlertTriangle
                                            className="w-3 h-3 text-yellow-500"
                                            title="Low price confidence"
                                          />
                                        )}
                                      </div>
                                    ) : (
                                      <div className="flex items-center gap-1">
                                        <span className="text-xs font-medium whitespace-nowrap">
                                          {formatPriceOrOddsPercent(
                                            pickTipPrice(formattedEntry),
                                          )}
                                        </span>
                                        {/* Warning icon for low confidence */}
                                        {entry?.effectivePriceConfidence ===
                                          "LOW" && (
                                          <AlertTriangle
                                            className="w-3 h-3 text-yellow-500"
                                            title="Low price confidence"
                                          />
                                        )}
                                      </div>
                                    )}
                                  </div>

                                  {/* Only show result container if there's a result */}
                                  {resultDisplay && (
                                    <div className="tip-result-container flex items-center justify-center min-w-[2rem] pl-2">
                                      {resultDisplay}
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      </div>

                      {/* Extra details section for early predictions - conditionally shown */}
                      {showExtraDetails && (
                        <HorseExtraDetails
                          entry={{
                            ...entry,
                            jockey:
                              typeof entry.jockey === "object"
                                ? entry.jockey?.fullName ||
                                  entry.jockey?.name ||
                                  null
                                : entry.jockey,
                            trainer:
                              typeof entry.trainer === "object"
                                ? entry.trainer?.fullName ||
                                  entry.trainer?.name ||
                                  null
                                : entry.trainer,
                          }}
                        />
                      )}
                    </div>
                  );
                })}
                {renderPriceToggle(firstFourData)}
              </div>
            );
          } catch (error) {
            logger.error("Error rendering early prediction data:", error);
            return (
              <div className="early-prediction-data-container mt-4 mb-2 p-3 bg-gray-50 rounded-lg border border-pink-200">
                <p className="text-sm text-center text-gray-500">
                  An error occurred while rendering early prediction data.
                </p>
              </div>
            );
          }
        })()}
    </>
  );
}
